/* cSpell:ignore openrouter OPENROUTER */
/**
 * Cortex Agent — Standalone long-session AI agent page
 *
 * OpenClaw-inspired persistent agent with:
 *  - Session management (create, resume, delete sessions)
 *  - Extended conversation context with auto-escalating model tier
 *  - Session metadata display (duration, message count)
 *  - Full-height chat UI designed for long work sessions
 *  - Converged quick actions (@flowstate/quick-actions): canonical + workflows + federated skills
 *  - Server-backed session persistence via TanStack Start server functions
 *  - Context summarization for long sessions
 *  - Multiplayer session task panel with [CORTEX_TASK:*] agent markers
 *  - Synaptic Awakening toasts for background runs
 *
 * Architecture: browser → brain edge function directly (no same-origin
 * /api/ai/cortex proxy). Folded in from the retired apps/cortex-agent app.
 */

import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import type { RouterContextWithSession } from "../../../router";
import { useState, useCallback, useRef, useEffect, useMemo, useDeferredValue } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { useCortexAgent } from "../../../hooks/useCortexAgent";
import type { CortexSession, SessionTask } from "../../../hooks/useCortexAgent";
import { Button } from "../../../components/ui/button";
import { InputRaw, ContextWindowUsage, ActivityFeed } from "@flowstate/ui";
import VoiceInputButton from "../../../components/voice-input/VoiceInputButton";
import { ModelPicker } from "../../../features/ai-agent/components/ModelPicker";
import { SessionTaskPanel } from "../../../features/ai-agent/components/SessionTaskPanel";
import { CortexBootScreen } from "../../../features/ai-agent/components/CortexBootScreen";
import { useContextWindowUsage } from "../../../features/ai-agent/hooks/useContextWindowUsage";
import { useSubmitWorkflowRun } from "../../../features/ai-agent/hooks/useSubmitWorkflowRun";
import useAISettings from "../../../hooks/useAISettings";
import { useCurrentOrganizationId } from "../../../hooks/useCurrentOrganization";
import { CortexAISetupWizard } from "@flowstate/cortex-auth";
import { supabase } from "../../../integrations/supabase/client";
import { toast } from "../../../components/ui/sonner";
import { DEFAULT_GEMINI_FLASH_MODEL } from "@flowstate/types";
import {
  quickActionsService,
  getWorkflowActions,
  getFederatedSkillActions,
  type QuickAction,
  type QuickActionGroup,
  type AgentContext,
  type FederatedSkillDTO,
} from "@flowstate/quick-actions";
import { fetchFederatedSkills } from "../../../server/cortex-skill-actions";
import { submitCortexJob } from "../../../server/cortex-jobs";
import { AgentWindow } from "../../../features/cortex/components/AgentWindow";

/** Feature flag — the unified workbench replaces the legacy session page. */
const CORTEX_WORKBENCH_ENABLED =
  (import.meta.env.VITE_CORTEX_WORKBENCH ?? "") === "true" ||
  (import.meta.env.VITE_CORTEX_WORKBENCH ?? "") === "1";

export const Route = createFileRoute("/_admin/cortex/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;
    // Session validated — Cortex agent manages its own state via useCortexAgent hook.
    // No server data to pre-fetch; sessions are created on-demand.
  },
  pendingComponent: () => <PageSkeleton rows={6} variant="list" />,
  errorComponent: createRouteError("Cortex Agent"),
  component: CortexAgentPage,
});

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "just now";
}

/** Strip [CORTEX_TASK:...]{...}[/CORTEX_TASK] action markers from displayed text. */
const CORTEX_TASK_MARKER_RE = /\[CORTEX_TASK:(?:create|complete|update)\][\s\S]*?\[\/CORTEX_TASK\]/g;
function stripActionMarkers(text: string): string {
  return text.replace(CORTEX_TASK_MARKER_RE, "").trim();
}

// ── Session List Sidebar ────────────────────────────────────────────────────

function SessionSidebar({
  sessions,
  activeSessionId,
  onNewSession,
  onResumeSession,
  onDeleteSession,
  serverPersistence,
  sessionTasks,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onPromoteTask,
  orgId,
}: {
  sessions: CortexSession[];
  activeSessionId: string | null;
  onNewSession: () => void;
  onResumeSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  serverPersistence: boolean;
  sessionTasks: SessionTask[];
  onAddTask: (opts: { title: string; description?: string; priority?: SessionTask["priority"] }) => Promise<void>;
  onEditTask: (taskId: string, updates: Partial<Pick<SessionTask, "title" | "description" | "status" | "priority">>) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onPromoteTask: (taskId: string, opts?: { assignTo?: string; dueDate?: string }) => Promise<string | null>;
  orgId: string | null;
}) {
  const [showActivityFeed, setShowActivityFeed] = useState(false);
  return (
    <aside className="w-full md:w-72 shrink-0 rounded-lg border bg-card flex flex-col overflow-hidden">
      <div className="p-3 border-b flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-card-foreground">
            Sessions
          </h2>
          {serverPersistence && (
            <span className="text-[10px] text-emerald-500 flex items-center gap-1 mt-0.5">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Server synced
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onNewSession}
          className="text-xs"
          aria-label="Start new session"
        >
          + New
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {sessions.length === 0 && (
          <div className="p-4 text-center">
            <p className="text-xs text-muted-foreground">
              No sessions yet. Click &quot;+ New&quot; or send a message to
              start your first session.
            </p>
          </div>
        )}

        <ul className="divide-y">
          {sessions.map((session) => (
            <li
              key={session.id}
              className={`group relative ${
                session.id === activeSessionId
                  ? "bg-primary/10"
                  : "hover:bg-muted/50"
              }`}
            >
              <button
                className="w-full text-left p-3 focus:outline-none focus:ring-2 focus:ring-primary/50 rounded min-h-[44px]"
                onClick={() => onResumeSession(session.id)}
                aria-label={`Resume session: ${session.title}`}
              >
                <p className="text-sm font-medium truncate text-foreground">
                  {session.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {session.messageCount} messages ·{" "}
                  {formatTimeAgo(session.lastActiveAt)}
                  {session.modelTier === "pro" && (
                    <span className="ml-1 text-primary">⚡ pro</span>
                  )}
                </p>
              </button>
              <button
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-destructive/50 rounded p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteSession(session.id);
                }}
                aria-label={`Delete session: ${session.title}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 6h18" />
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Session tasks panel — only when a session is active */}
      {activeSessionId && (
        <SessionTaskPanel
          tasks={sessionTasks}
          onAdd={onAddTask}
          onEdit={onEditTask}
          onDelete={onDeleteTask}
          onPromote={onPromoteTask}
        />
      )}

      {/* Activity feed toggle — org-scoped live activity */}
      {orgId && (
        <div className="border-t">
          <button
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors min-h-[36px]"
            onClick={() => setShowActivityFeed((v) => !v)}
            aria-expanded={showActivityFeed ? "true" : "false"}
            aria-controls="cortex-activity-feed"
          >
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              Activity
            </span>
            <span className="opacity-50">{showActivityFeed ? "▴" : "▾"}</span>
          </button>
          {showActivityFeed && (
            <div id="cortex-activity-feed" className="max-h-64 overflow-y-auto">
              <ActivityFeed
                orgId={orgId}
                maxItems={20}
                compact
                showFilters={false}
                className="border-0 rounded-none shadow-none"
              />
            </div>
          )}
        </div>
      )}
    </aside>
  );
}

// ── Session Header ──────────────────────────────────────────────────────────

function SessionHeader({
  session,
  duration,
  contextUsage,
  onRename,
  onSummarize,
  onExport,
}: {
  session: CortexSession | null;
  duration: number;
  contextUsage: ReturnType<typeof useContextWindowUsage>;
  onRename: (title: string) => void;
  onSummarize: () => void;
  onExport: (format: "markdown" | "json") => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (!session) return null;

  const handleSubmitRename = () => {
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== session.title) {
      onRename(trimmed);
    }
    setIsEditing(false);
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2 border-b bg-card">
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={handleSubmitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmitRename();
              if (e.key === "Escape") setIsEditing(false);
            }}
            className="text-sm font-medium bg-transparent border-b border-primary/50 focus:outline-none focus:border-primary w-full"
            aria-label="Session title"
          />
        ) : (
          <button
            onClick={() => {
              setEditTitle(session.title);
              setIsEditing(true);
            }}
            className="text-sm font-medium text-foreground truncate hover:underline focus:outline-none focus:underline"
            title="Click to rename session"
          >
            {session.title}
          </button>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
        <ContextWindowUsage
          inputTokens={contextUsage.inputTokens}
          outputTokens={contextUsage.outputTokens}
          maxTokens={contextUsage.maxTokens}
          modelName={contextUsage.modelName}
        />
        <span>{session.messageCount} msgs</span>
        <span className="text-primary/70">{formatDuration(duration)}</span>
        {session.messageCount > 10 && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={onSummarize}
              className="text-xs h-7 px-2"
              title="Summarize session context for better long-term recall"
              aria-label="Summarize session context"
            >
              📋 Summarize
            </Button>
            <div className="relative group">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 px-2"
                aria-label="Export session"
              >
                💾 Export
              </Button>
              <div className="absolute right-0 top-full mt-1 w-32 rounded-lg border bg-card shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                <button
                  onClick={() => onExport("markdown")}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors rounded-t-lg"
                >
                  Markdown
                </button>
                <button
                  onClick={() => onExport("json")}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors rounded-b-lg"
                >
                  JSON
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Cortex Quick Actions (converged on @flowstate/quick-actions) ─────────────
//
// The 2025 static DOMAIN_ACTIONS grid has been retired. Cortex uses the
// canonical quickActionsService + executable WORKFLOW_REGISTRY actions +
// federated agent_skills palette.

/** Cortex is a single-surface chat app — no route-derived domain expertise. */
const CORTEX_AGENT_CONTEXT: AgentContext = {
  role: undefined,
  roles: [],
  scope: "site",
  capabilities: {
    canSeeAdminData: false,
    canPerformAdminActions: false,
    canManageUsers: false,
    canViewAuditLogs: false,
    canCreateTodos: true,
    canEditTodos: true,
    canExportData: true,
    canUseAdvancedSearch: true,
    braindumpDailyLimit: 100,
    braindumpMonthlyLimit: 1000,
  },
  expertise: undefined,
};

function CortexEmptyState({
  actions,
  groupedActions,
  onActionExecute,
  onPrompt,
}: {
  actions: QuickAction[];
  groupedActions: QuickActionGroup[];
  onActionExecute: (action: QuickAction) => void;
  onPrompt: (text: string) => void;
}) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center h-full text-center space-y-6 px-4 py-6">
      {/* Hero */}
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-primary"
        >
          <path d="M12 2a8 8 0 0 0-8 8c0 3.4 2.1 6.3 5 7.5V20a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-2.5c2.9-1.2 5-4.1 5-7.5a8 8 0 0 0-8-8z" />
          <path d="M10 22h4" />
          <path d="M9.5 14.5 12 12l2.5 2.5" />
          <path d="M12 12v4" />
        </svg>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          Cortex Agent
        </h3>
        <p className="text-sm text-muted-foreground max-w-md mt-1">
          Your AI assistant for all FlowState domains. Sessions persist across
          refreshes and can run for hours — designed for deep work.
        </p>
      </div>

      {/* Quick start prompts */}
      <div className="flex flex-wrap gap-2 justify-center">
        {[
          "Plan my week based on current priorities",
          "Break down this project into tasks",
          "Review my open items and suggest next steps",
        ].map((prompt) => (
          <Button
            key={prompt}
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => onPrompt(prompt)}
          >
            {prompt}
          </Button>
        ))}
      </div>

      {/* Converged quick actions grid (canonical + workflows + federated) */}
      {actions.length > 0 && (
        <div className="w-full max-w-2xl space-y-4 text-left">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider text-center">
            Quick actions
          </p>
          {groupedActions.map((group) => (
            <div key={group.category}>
              <p className="text-xs font-medium text-muted-foreground mb-2">
                {group.label}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {group.actions.map((action) => {
                  const Icon = action.icon;
                  const isWorkflow = action.kind === "workflow-run";
                  return (
                    <button
                      key={action.id}
                      className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors text-left group min-h-[44px]"
                      onClick={() => onActionExecute(action)}
                    >
                      {Icon && (
                        <span className="shrink-0 mt-0.5 text-muted-foreground group-hover:text-foreground transition-colors">
                          <Icon size={18} />
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {action.label}
                          {isWorkflow && (
                            <span className="ml-1.5 text-[10px] text-primary font-normal">
                              ▶ run
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {action.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Navigation shortcut */}
      <div className="flex justify-center pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-xs text-muted-foreground"
          onClick={() => navigate({ to: "/dashboard" })}
        >
          Or go to Dashboard →
        </Button>
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

function CortexAgentPage() {
  const { user, session: authSession, loading, isAuthenticated } = useAuth();
  const { settings, updateSettings, isCortexReady } = useAISettings();
  const [input, setInput] = useState("");
  const inputRef = useRef("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const orgId = useCurrentOrganizationId();

  // Model selection state — persisted to localStorage
  const [selectedProvider, setSelectedProvider] = useState<"gemini" | "openrouter">(() => {
    try {
      return (localStorage.getItem("cortex-provider") as "gemini" | "openrouter") || "gemini";
    } catch {
      return "gemini";
    }
  });
  const [selectedModel, setSelectedModel] = useState<string>(() => {
    try {
      return localStorage.getItem("cortex-model") || "";
    } catch {
      return "";
    }
  });

  const persistProvider = useCallback((p: "gemini" | "openrouter") => {
    setSelectedProvider(p);
    try { localStorage.setItem("cortex-provider", p); } catch { /* noop */ }
  }, []);
  const persistModel = useCallback((m: string) => {
    setSelectedModel(m);
    try { localStorage.setItem("cortex-model", m); } catch { /* noop */ }
  }, []);

  const cortex = useCortexAgent({
    provider: selectedProvider,
    model: selectedModel || undefined,
  });
  const contextUsage = useContextWindowUsage(
    cortex.messages,
    selectedModel || DEFAULT_GEMINI_FLASH_MODEL,
    selectedProvider,
  );
  const { submitWorkflowRun } = useSubmitWorkflowRun();

  // ── Converged quick actions (canonical + workflows + federated) ────────
  const [federatedSkills, setFederatedSkills] = useState<FederatedSkillDTO[]>([]);

  // Curated/global skills on auth — no embedding round-trip.
  useEffect(() => {
    if (!authSession?.access_token) return;
    let cancelled = false;
    fetchFederatedSkills({ data: { accessToken: authSession.access_token } })
      .then((result) => {
        if (cancelled) return;
        if (result.skills.length > 0) {
          setFederatedSkills(result.skills);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[Cortex] Federated skills fetch failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [authSession?.access_token]);

  // Semantic (ontology-enriched) skill match only after a long pause on a
  // substantial prompt — avoids Gemini embed cost on every keystroke.
  useEffect(() => {
    if (!authSession?.access_token) return;
    const probeText = input.trim();
    if (probeText.length < 24) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchFederatedSkills({
        data: { accessToken: authSession.access_token, probeText },
      })
        .then((result) => {
          if (cancelled) return;
          if (result.skills.length > 0) {
            setFederatedSkills(result.skills);
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("[Cortex] Semantic federated skills fetch failed:", err);
        });
    }, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [authSession?.access_token, input]);

  // Merge canonical + workflow + federated actions, then group by category
  const { cortexActions, cortexGroupedActions } = useMemo(() => {
    const canonical = quickActionsService.generateActions(
      CORTEX_AGENT_CONTEXT,
      {} as Record<string, unknown>,
      undefined
    );
    const workflowActions = getWorkflowActions();
    const federatedActions = getFederatedSkillActions(federatedSkills);
    const merged = [...canonical, ...workflowActions, ...federatedActions];
    const grouped = quickActionsService.groupActionsByCategory(merged);
    return { cortexActions: merged, cortexGroupedActions: grouped };
  }, [federatedSkills]);

  // Dispatch quick action: workflow-run → submit to queue; prompt → send to chat
  const handleActionExecute = useCallback(
    async (action: QuickAction) => {
      if (action.kind === "workflow-run" && action.workflowId) {
        const result = await submitWorkflowRun(action.workflowId);
        if (result.error) {
          toast.error(result.error);
        } else {
          toast.success(`Workflow "${action.label}" queued`);
        }
      } else if (action.prompt) {
        setInput("");
        inputRef.current = "";
        await cortex.sendMessage(action.prompt, { skillSlug: action.skillSlug });
      }
    },
    [submitWorkflowRun, cortex]
  );

  // Background mode — persisted to localStorage
  const [backgroundMode, setBackgroundMode] = useState(() => {
    try {
      return localStorage.getItem('cortex-background-mode') === 'true';
    } catch {
      return false;
    }
  });

  const handleToggleBackgroundMode = useCallback(() => {
    setBackgroundMode(prev => {
      const next = !prev;
      try {
        localStorage.setItem('cortex-background-mode', String(next));
      } catch {
        // localStorage unavailable
      }
      return next;
    });
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [cortex.messages]);

  // Stable refs for handleSend so it doesn't recreate on every state change
  const isLoadingRef = useRef(cortex.isLoading);
  isLoadingRef.current = cortex.isLoading;
  const sendMessageRef = useRef(cortex.sendMessage);
  sendMessageRef.current = cortex.sendMessage;
  const backgroundModeRef = useRef(backgroundMode);
  backgroundModeRef.current = backgroundMode;
  const authSessionRef = useRef(authSession);
  authSessionRef.current = authSession;
  const userRef = useRef(user);
  userRef.current = user;

  const handleSend = useCallback(async () => {
    const text = inputRef.current.trim();
    if (!text || isLoadingRef.current) return;

    const bg = backgroundModeRef.current;
    const session = authSessionRef.current;
    const uid = userRef.current;

    // One semantic skill refresh on submit (cached server-side) so the next
    // turn's palette reflects the sent prompt without keystroke embeds.
    if (session?.access_token && text.length >= 24) {
      void fetchFederatedSkills({
        data: { accessToken: session.access_token, probeText: text },
      })
        .then((result) => {
          if (result.skills.length > 0) setFederatedSkills(result.skills);
        })
        .catch(() => {
          /* non-fatal */
        });
    }

    if (bg && session?.access_token && uid?.id) {
      setInput("");
      inputRef.current = "";
      try {
        const result = await submitCortexJob({
          data: {
            accessToken: session.access_token,
            userId: uid.id,
            prompt: text,
          },
        });
        if (result.error) {
          // eslint-disable-next-line no-console
          console.error("[CortexAgent] Background job failed:", result.error);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[CortexAgent] Background job error:", err);
      }
      return;
    }

    setInput("");
    inputRef.current = "";
    await sendMessageRef.current(text);
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearch = useDeferredValue(searchQuery);

  const filteredMessages = useMemo(() => {
    if (!deferredSearch.trim()) return cortex.messages;
    const q = deferredSearch.toLowerCase();
    return cortex.messages.filter((m) => m.content.toLowerCase().includes(q));
  }, [cortex.messages, deferredSearch]);

  // Boot animation — plays once per browser tab session.
  const [bootComplete, setBootComplete] = useState(() => {
    try {
      return typeof window !== "undefined" && sessionStorage.getItem("cortex.booted") === "1";
    } catch {
      return false;
    }
  });
  const handleBootComplete = useCallback(() => {
    setBootComplete(true);
    try {
      sessionStorage.setItem("cortex.booted", "1");
    } catch { /* noop */ }
  }, []);

  // Show boot screen while loading auth or playing boot animation
  if (loading || !bootComplete) {
    return <CortexBootScreen onComplete={handleBootComplete} />;
  }

  // Auth resolved but not authenticated — beforeLoad redirects to /auth
  if (!isAuthenticated || !user || !authSession || !supabase) {
    return null;
  }

  // Credential gate: chat is unavailable until the user has configured a
  // Gemini or OpenRouter credential (OAuth or BYOK).
  if (!isCortexReady) {
    return (
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="w-full max-w-xl">
          <CortexAISetupWizard
            supabase={supabase}
            accessToken={authSession.access_token}
            supabaseUrl={import.meta.env.VITE_SUPABASE_URL ?? ""}
            settings={settings}
            onSettingsChanged={updateSettings}
            mode="gate"
          />
        </div>
      </div>
    );
  }

  // Unified agent window (flag-gated rollout) — session rail + timeline +
  // workbench panes powered by useCortexEngine + cortex_run_events.
  if (CORTEX_WORKBENCH_ENABLED) {
    return (
      <div className="h-full min-h-0">
        <AgentWindow mode="workbench" initialKind="work" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header banner */}
      <div className="bg-primary/5 border-b px-4 py-2 flex items-center gap-2">
        <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-xs font-medium text-foreground">
          Cortex Agent
        </span>
        <span className="text-xs text-muted-foreground hidden sm:inline">
          — Standalone AI assistant · all domains · streaming enabled
        </span>
        <div className="ml-auto flex items-center gap-2">
          <ModelPicker
            selectedProvider={selectedProvider}
            onProviderChange={persistProvider}
            selectedModel={selectedModel}
            onModelChange={persistModel}
            disabled={cortex.isLoading}
            variant="pill"
          />
          {cortex.messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={cortex.clear}
              className="text-xs"
            >
              Clear chat
            </Button>
          )}
        </div>
      </div>

      <main className="flex-1 flex flex-col md:flex-row gap-0 min-h-0">
        {/* Chat area */}
        <div className="flex-1 flex flex-col min-h-0">
          <SessionHeader
            session={cortex.session}
            duration={cortex.sessionDuration}
            contextUsage={contextUsage}
            onRename={cortex.renameSession}
            onSummarize={cortex.summarizeContext}
            onExport={cortex.exportSession}
          />

          <section className="flex-1 flex flex-col min-h-0">
            {/* Search bar */}
            {cortex.messages.length > 5 && (
              <div className="px-4 pt-2 pb-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search messages…"
                  className="w-full h-7 rounded-md border border-border bg-transparent px-2.5 text-xs placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
                  aria-label="Search messages"
                />
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
              {/* Empty state with converged quick actions */}
              {filteredMessages.length === 0 && !cortex.isLoading && !searchQuery.trim() && (
                <CortexEmptyState
                  actions={cortexActions}
                  groupedActions={cortexGroupedActions}
                  onActionExecute={handleActionExecute}
                  onPrompt={setInput}
                />
              )}

              {/* No search results */}
              {filteredMessages.length === 0 && searchQuery.trim() && !cortex.isLoading && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-sm text-muted-foreground">No messages match your search</p>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="text-xs text-primary hover:underline mt-1"
                  >
                    Clear search
                  </button>
                </div>
              )}

              {/* Messages */}
              {filteredMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`rounded-lg px-4 py-3 text-sm ${
                    msg.role === "user"
                      ? "bg-primary/10 text-primary-foreground ml-8"
                      : "bg-muted text-muted-foreground mr-8"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-xs uppercase tracking-wider text-foreground">
                      {msg.role === "user" ? "You" : "Cortex"}
                    </span>
                  </div>
                  {/* Render thinking parts as collapsible */}
                  {msg.parts?.length ? (
                    <div className="whitespace-pre-wrap leading-relaxed">
                      {msg.parts.map((part, pi) => {
                        if (part.type === "thinking") {
                          return (
                            <details key={pi} className="mb-2 group">
                              <summary className="text-[11px] cursor-pointer text-muted-foreground hover:text-foreground select-none">
                                <span className="inline-flex items-center gap-1">
                                  <span className="opacity-50 group-open:opacity-100 transition-opacity">🧠</span>
                                  Reasoning
                                </span>
                              </summary>
                              <div className="mt-1 pl-3 text-xs text-muted-foreground/80 border-l-2 border-muted-foreground/20 italic">
                                {part.content}
                              </div>
                            </details>
                          );
                        }
                        return (
                          <span key={pi}>{stripActionMarkers(part.content as string)}</span>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap leading-relaxed">
                      {typeof msg.content === "string" ? stripActionMarkers(msg.content) : msg.content}
                    </div>
                  )}
                </div>
              ))}

              {/* Loading indicator */}
              {cortex.isLoading && (
                <div className="rounded-lg px-4 py-3 text-sm bg-muted text-muted-foreground mr-8">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-xs uppercase tracking-wider text-foreground">
                      Cortex
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:0ms]"
                    />
                    <span
                      className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:150ms]"
                    />
                    <span
                      className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:300ms]"
                    />
                  </div>
                </div>
              )}

              {/* Error display */}
              {cortex.error && (
                <div className="rounded-lg px-4 py-3 text-sm bg-destructive/10 text-destructive mr-8">
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span className="font-medium">Error:</span>{" "}
                    {cortex.error.message}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div className="p-4 border-t bg-card">
              {backgroundMode && (
                <div className="mb-2 flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-1.5 text-xs text-amber-600 dark:text-amber-400">
                  <span>⚡</span>
                  <span>Background mode — Cortex will notify you when complete</span>
                </div>
              )}
              <div className="flex gap-2">
                <div className="flex-1 flex items-center gap-1">
                  <VoiceInputButton
                    onTranscription={(text) => { setInput((prev) => prev + text); inputRef.current = inputRef.current + text; }}
                    isDisabled={cortex.isLoading}
                  />
                  <InputRaw
                    type="text"
                    value={input}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setInput(e.target.value); inputRef.current = e.target.value; }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                      e.key === "Enter" && !e.shiftKey && handleSend()
                    }
                    placeholder={
                      backgroundMode
                        ? "Describe what you want done — results via notification…"
                        : cortex.session
                          ? "Continue the conversation…"
                          : "Start a new Cortex session…"
                    }
                    className="flex-1"
                    disabled={cortex.isLoading}
                    aria-label="Message input"
                  />
                </div>
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || cortex.isLoading}
                >
                  {cortex.isLoading ? "…" : backgroundMode ? "Submit Job" : "Send"}
                </Button>
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-[10px] text-muted-foreground text-center flex-1">
                  {backgroundMode
                    ? "Jobs run in the background — close the browser and return to results."
                    : "Sessions are persistent and can run for hours. Model auto-escalates for complex requests."}
                </p>
                <Button
                  variant="ghost"
                  onClick={handleToggleBackgroundMode}
                  className={`h-auto inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                    backgroundMode
                      ? "bg-amber-500/10 text-amber-600 border border-amber-500/20 dark:text-amber-400"
                      : "text-muted-foreground hover:bg-muted"
                  }`}
                  aria-label={backgroundMode ? "Disable background mode" : "Enable background mode"}
                >
                  ⚡ {backgroundMode ? "Background: On" : "Background"}
                </Button>
              </div>
            </div>
          </section>
        </div>

        {/* Session sidebar */}
        <SessionSidebar
          sessions={cortex.sessions}
          activeSessionId={cortex.session?.id ?? null}
          onNewSession={() => cortex.startSession()}
          onResumeSession={cortex.resumeSession}
          onDeleteSession={cortex.deleteSession}
          serverPersistence={cortex.serverPersistence}
          sessionTasks={cortex.sessionTasks}
          onAddTask={cortex.addSessionTask}
          onEditTask={cortex.editSessionTask}
          onDeleteTask={cortex.removeSessionTask}
          onPromoteTask={cortex.promoteSessionTask}
          orgId={orgId}
        />
      </main>
    </div>
  );
}
